import { useState, useEffect, useRef } from 'react'
import { ReelMark, REEL_LABELS } from './ReelRating'
import { isOverlayBusy } from './Celebration'
import { railBusy, claimRail, releaseRail, subscribeRail } from '../lib/toastRail'

const RAIL_ID = 'rating-saved'

export function RatingSavedToast() {
  const [detail,  setDetail]  = useState(null)
  const [visible, setVisible] = useState(false)
  const [cycle,   setCycle]   = useState(0)   // replays the time bar on each new rating
  const [gate,    setGate]    = useState(0)   // forces a re-check when the overlay clears
  const timerRef = useRef(null)
  const BAR_MS = 2800

  useEffect(() => {
    const handler = (e) => {
      const { show, stars } = e.detail || {}
      if (!show || !stars) return
      clearTimeout(timerRef.current)
      setDetail({ show, stars })
      setVisible(true)
      setCycle(c => c + 1)
    }
    window.addEventListener('nextep:rating-saved', handler)
    return () => window.removeEventListener('nextep:rating-saved', handler)
  }, [])

  // Defer while a full-screen sheet/cinematic owns the screen — same hold as Celebration
  // and the top toast rail, so this passive confirmation never stacks on an update sheet.
  // ...and defer to whoever already holds the bottom rail, so this never lands on top of
  // an undo/celebration/pulse toast at nearly the same offset (Jason 2026-07-27).
  const held = !!detail && (isOverlayBusy() || railBusy(RAIL_ID))

  // Recover once the overlay clears: mirror Celebration's poll + wrap-done/card-done
  // listeners. Without this, a rating saved while an overlay was up was dropped
  // forever — the hide timer ran down behind the overlay and it never showed
  // (audit 2026-07-22). The visible-timeout is (re)started only once we're actually
  // on screen, so the 2.8s bar reflects real visible time, not time spent held.
  useEffect(() => {
    if (held) {
      const iv = setInterval(() => setGate(g => g + 1), 450)
      const wake = () => setGate(g => g + 1)
      window.addEventListener('nextep:wrap-done', wake)
      window.addEventListener('nextep:card-done', wake)
      return () => { clearInterval(iv); window.removeEventListener('nextep:wrap-done', wake); window.removeEventListener('nextep:card-done', wake) }
    }
    // On screen (or nothing to show): run the auto-hide countdown.
    if (detail && visible) {
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setVisible(false), BAR_MS)
      return () => clearTimeout(timerRef.current)
    }
  }, [held, detail, visible, cycle])

  // Hold the rail exactly while we're on screen; release unconditionally on unmount so a
  // stuck holder can never silence the rail.
  useEffect(() => {
    if (detail && !held) claimRail(RAIL_ID); else releaseRail(RAIL_ID)
    return () => releaseRail(RAIL_ID)
  }, [detail, held, visible])
  // Re-check the moment the rail frees up, rather than waiting for the next poll tick.
  useEffect(() => subscribeRail(() => setGate(g => g + 1)), [])

  if (!detail || held) return null

  const key   = Math.round(detail.stars * 2) / 2
  const cfg   = REEL_LABELS[key] || REEL_LABELS[0]

  return (
    <div style={{
      position:'fixed', bottom:'calc(var(--nav-h) + 10px)',
      left:16, right:16, zIndex:'var(--z-toast)',   // was a raw 8500 (off the token scale)
      display:'flex', justifyContent:'center',
      pointerEvents:'none',
      transition:'opacity 0.25s ease, transform var(--dur-base) var(--ease-soft), filter 0.3s ease',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      filter: visible ? 'blur(0)' : 'blur(4px)',
    }}>
      <div style={{
        position:'relative', overflow:'hidden',
        background:`linear-gradient(135deg, color-mix(in srgb, ${cfg.color} 14%, #14141f), color-mix(in srgb, ${cfg.color} 6%, #0d0d16))`,
        border:`1px solid color-mix(in srgb, ${cfg.color} 40%, transparent)`,
        borderRadius:'var(--toast-radius)',
        padding:'12px 16px 13px',
        display:'flex', alignItems:'center', gap:12,
        boxShadow:`0 14px 38px rgba(0,0,0,0.55), 0 4px 18px ${cfg.color}29`,
        fontFamily:'var(--font)',
        maxWidth:320,
      }}>
        {/* Single reel icon in the standard toast chip, matching every other toast */}
        <span style={{
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
          width:'var(--toast-chip-size)', height:'var(--toast-chip-size)', borderRadius:'var(--radius-full)',
          background:`${cfg.color}22`, border:`1px solid ${cfg.color}59`,
        }}>
          <ReelMark value={detail.stars} size={18} />
        </span>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:cfg.color, fontSize:'var(--toast-eyebrow-size)', fontWeight:'var(--toast-eyebrow-weight)', letterSpacing:'var(--toast-eyebrow-tracking)', textTransform:'uppercase', marginBottom:2 }}>
            {cfg.label}
          </div>
          <div style={{ color:'var(--text)', fontSize:13.5, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {detail.show.name} · {detail.stars}/5
          </div>
        </div>

        {visible && (
          <div key={cycle} style={{
            position:'absolute', left:0, bottom:0, height:2.5, width:'100%',
            background:`linear-gradient(90deg, ${cfg.color}, ${cfg.color}77)`,
            transformOrigin:'left center', opacity:0.85,
            '--nxa':`toastBar ${BAR_MS}ms linear forwards`,
          }}/>
        )}
        
      </div>
    </div>
  )
}
